import { createModel } from '@rematch/core';
import _ from 'lodash';

import { RootModel } from '../index';
import { apiUrls, replaceUrls } from '../../api/apiUrls';
import { get, post } from '../../api/api';
import RacingState, { RaceMarket } from '../../interfaces/RacingState';

export default createModel<RootModel>()({
  state: {
    meeting: {
      name: '',
      country: '',
      countryCode: '',
      type: 0,
      events: [],
      cardCode: '',
      couponCode: null,
      calendar: []
    },
    retailErrors: { betcode:'', race:'', betType:'', starters:'', stake:'' },
    retailValues: { betCode:'', race:'CR', betType:'', starters:'', stake:'' },
    crossEvents: [],
    selectedTab: 'today'
  } as RacingState,

  reducers: {
    setMeeting(state, payload: RacingState['meeting']) {
      return { ...state, meeting: payload };
    },

    setErrors(state, payload: RacingState['retailErrors']){
      return { ...state, retailErrors:payload };
    },

    setValues(state, payload: RacingState['retailValues']){
      return { ...state, retailValues:payload };
    },

    setCrossEvents(state, payload: RacingState['crossEvents']) {
      return { ...state, crossEvents: payload };
    },

    populateEvents(state, payload: RacingState['meeting']['events']) {
      return { ...state, meeting: { ...state.meeting, events: payload } };
    },

    setSelectedTab(state, payload) {
      return { ...state, selectedTab: payload };
    },

    addMeetingCalendar(state, payload: RacingState['meeting']['events']){
      return { ...state, calendar: payload };
    }
  },

  effects: (dispatch) => ({
    async getMeetingByCode(payload): Promise<boolean> {
      try {
        if (!payload.hideLoading) dispatch.events.setLoading(true);
        const response = await get(replaceUrls(apiUrls.racingEventByCode, { meetingCode: payload.meetingCode, event: payload.event }));

        if (response && response.status === 200) {
          dispatch.racing.setMeeting(response.data.results);
          return true;
        }

        if (!payload.hideLoading) dispatch.events.setLoading(false);
        return false;
      } catch (error) {
        if (!payload.hideLoading) dispatch.events.setLoading(false);
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
        return false;
      }
    },

    async getMeetingInfo(payload){
      try {
        const response = await get(replaceUrls(apiUrls.meetingInfo, { meetingCode: payload.meetingCode }));
        if (response.status === 200){
          dispatch.racing.addMeetingCalendar(response.data.results?.events);
        }
      } catch (e){
        //Todo: handle error
      }
    },

    async changeErrors (payload, state) {
      const { value, error } = payload;

      if (value === ''){
        dispatch.racing.setErrors({ betcode:'', race:'', betType:'', starters:'', stake:'' });
      } else {
        const errors = { ...state.retailErrors, [value]: error };
        dispatch.racing.setErrors(errors);
      }
    },

    async changeValues (payload, state) {
      const { key, value } = payload;

      const values = { ...state.racing.retailValues, [key]: value };
      dispatch.racing.setValues(values);
    },

    async getCrossEvents(payload): Promise<boolean> {
      try {
        dispatch.events.setLoading(true);
        const response = await post(apiUrls.racingCrossEvents, { crossEvents: payload });
        const newCrossEvents = [];

        if (response && response.status === 200) {
          for (const crossEvent of response.data.results) {
            newCrossEvents.push(
              {
                eventCode: crossEvent.raceId,
                parentEvent: crossEvent
              }
            );
          }
          dispatch.racing.setCrossEvents(newCrossEvents);
          return true;
        }

        dispatch.events.setLoading(false);
        return false;
      } catch (error) {
        dispatch.events.setLoading(false);
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
        return false;
      }
    },

    async getFixedOddsCompetitors(payload): Promise<void> {
      try {
        dispatch.events.setLoading(true);
        const response = await get(
          replaceUrls(apiUrls.racingFixedOddsCompetitors, { meeting: payload.meeting })
        );

        if (response && response.status === 200) {
          dispatch.racing.populateEvents(response.data.results);
        }

        dispatch.events.setLoading(false);
      } catch (error) {
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
        dispatch.events.setLoading(false);
      }
    },

    async getRaceInfo(payload): Promise<void> {
      try {
        const response = await get(
          replaceUrls(apiUrls.getRaceInfo, { meeting: payload.eventCode })
        );
        return response.data.results;
      } catch (error) {
        // TODO: HANDLE ERROR
      }
    },

    async getNextRaces(hideLoading: boolean): Promise<void> {
      try {
        if (!hideLoading) dispatch.events.setLoading(true);
        const response = await get(apiUrls.nextRaces);

        if (response && response.status === 200) {
          dispatch.events.setNextRaces(response.data.results);
        }

        if (!hideLoading) dispatch.events.setLoading(false);
      } catch (error) {
        if (!hideLoading) dispatch.events.setLoading(false);
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
      }
    },

    clearMeeting() {
      dispatch.racing.setMeeting({
        name: '',
        country: '',
        countryCode: '',
        type: 0,
        events: [],
        cardCode: ''
      });
    }
  }),

  selectors: (slice, createSelector) => ({
    selectedEvent() {
      return slice(state => {
        return _.find(state.meeting.events, e => e.selectedEvent === true) || state.meeting.events[0];
      });
    },

    baseMarket() {
      return createSelector(
        slice,
        this.selectedEvent,
        (state, selectedEvent) => {
          if (selectedEvent) {
            const foMarkets: RaceMarket[] = _.filter(selectedEvent['markets'], (m: RaceMarket) => m.oddsType === 3);
            const tpMarkets: RaceMarket[] = _.filter(selectedEvent['markets'], (m: RaceMarket) => m.oddsType === 2);
            return _.find(foMarkets, m => m.typeCode === 20100 || m.genericName === 'FO WIN') || tpMarkets[0] || null; // FO WIN market
          } else {
            return null;
          }
        }
      );
    }
  })
});
